import {
  Component,
  contentChild,
  ElementRef,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from '@angular/material/dialog';
import { NotificationService } from '../../services/notification/notification.service';
import { CommonModule } from '@angular/common';
import { SessionStorageService } from '../../services/storage/session-storage/session-storage.service';
import { ChatMessageComponent } from './chat-message/chat-message.component';
import { GenericDialogBox } from '../generic-dialog-box/generic-dialog-box.component';
import { AskMeService } from '../../services/ask-me/ask-me.service';
import { COMMON_POPUP } from 'src/app/common/constants/popup.constants';
import {
  ASK_ME_ASSISTANT,
  ASK_ME_ERROR_MESSAGE,
  ASK_ME_INPUT_PLACEHOLDER,
  ASK_ME_USER,
  ASK_ME_WORKING_ON_RESPONSE,
} from 'src/app/components/projects/common/projects.constants';
import { Router } from '@angular/router';
import { GPT_5_MODEL, LOW } from 'src/app/common/constants/app.constants';
import { DIALOG_CONTENT_TYPE } from '../generic-dialog-box/generic-dialog-box.interface';

@Component({
    selector: 'app-ask-me',
    imports: [FormsModule, CommonModule, ChatMessageComponent],
    templateUrl: './ask-me.component.html',
    styleUrl: './ask-me.component.scss'
})
export class AskMeComponent implements OnInit {
  @ViewChild('inputRef') inputElement!: ElementRef;
  @ViewChild('chatHistory') private chatHistory!: ElementRef;

  userQuestion: string = '';
  sessionId: string = '';
  sessionStartedTime: string = '';
  suggestedQuestions: string[] = [];
  chatStarted = false;
  messages = [];
  userName: string;
  isLoading: boolean = false;
  showArrow: boolean = false;
  inputPlaceholder: string = ASK_ME_INPUT_PLACEHOLDER;
  reasoningParameter: string = LOW;
  llmModel: string = GPT_5_MODEL;

  constructor(
    private dialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<AskMeComponent>,
    private readonly router: Router,
    public readonly notificationService: NotificationService,
    private readonly sessionStorageService: SessionStorageService,
    private readonly askMeService: AskMeService,
    private elRef: ElementRef
  ) {}

  /**
   * Lifecycle hook that is called after data-bound properties are initialized.
   * Initializes input focus, fetches session data, and sets user name.
   */
  ngOnInit() {
    const userDetails = this.sessionStorageService.getValue('userDetails');

    this.userName = JSON.parse(JSON.stringify(userDetails))?.name;
    setTimeout(() => {
      this.inputElement?.nativeElement.focus();
    });
    this.askMeService.getSession().subscribe({
      next: (data) => {
        this.sessionId = data?.sessionId;
        this.suggestedQuestions = data?.faqs;
        this.sessionStartedTime = new Date().toISOString();
      },
    });
  }

  /**
   * Lifecycle hook that is called after the component's view has been checked.
   * Scrolls the chat history to the bottom.
   */
  ngAfterViewChecked() {
    this.scrollToBottom();
  }

  /**
   * Handles the ask action when the user submits a question.
   * Pushes the user's question to the messages array, calls the askMe service,
   * and handles the assistant's response or error.
   */
  onAsk = (): void => {
    this.isLoading = true;
    if (this.userQuestion.trim()) {
      if (!this.chatStarted) this.chatStarted = true;

      this.messages.push({
        text: this.userQuestion,
        sender: ASK_ME_USER,
        timestamp: new Date(),
      });

      const now = new Date().toISOString();

      const assistantMessage = {
        text: ASK_ME_WORKING_ON_RESPONSE,
        sender: ASK_ME_ASSISTANT,
        timestamp: new Date(),
      };
      this.messages.push(assistantMessage);
      const assistantIndex = this.messages.length - 1;

      this.askMeService.askMe(this.userQuestion, this.sessionId, now, this.llmModel, this.reasoningParameter).subscribe(
        (data) => {
          this.isLoading = false;
          this.messages[assistantIndex].text = data?.answer || '';
          this.messages[assistantIndex].timestamp = new Date();
        },
        (error) => {
          this.isLoading = false;
          this.messages[assistantIndex].text = ASK_ME_ERROR_MESSAGE;
          this.messages[assistantIndex].timestamp = new Date();
        }
      );

      this.userQuestion = '';
    }
  };

  /**
   * Opens a confirmation dialog to close the Ask Me session.
   * If confirmed, deletes the session and closes the dialog.
   */
  closeDialog = (): void => {
    const dialogRef = this.dialog.open(GenericDialogBox, {
      data: {
        title: COMMON_POPUP.ASK_ME_CLOSE.TITLE,
        message: COMMON_POPUP.ASK_ME_CLOSE.MESSAGE,
        info: COMMON_POPUP.ASK_ME_CLOSE.INFO,
        primaryButton: 'Close',
        secondaryButton: COMMON_POPUP.CANCEL,
      },
      panelClass: 'generic-close-dialog',
      disableClose: true,
      width: '468px',
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.askMeService.deleteSession(this.sessionId).subscribe(() => {
          this.sessionId = '';
        });
        this.dialogRef.close();
      }
    });
  };

  /**
   * Opens a confirmation dialog to reset the Ask Me session.
   * If confirmed, deletes the session, fetches a new session, and resets state.
   */
  reset = (): void => {
    const dialogRef = this.dialog.open(GenericDialogBox, {
      data: {
        title: COMMON_POPUP.ASK_ME_RESET.TITLE,
        message: COMMON_POPUP.ASK_ME_RESET.MESSAGE,
        info: COMMON_POPUP.ASK_ME_RESET.INFO,
        primaryButton: COMMON_POPUP.RESET,
        secondaryButton: COMMON_POPUP.CANCEL,
      },
      panelClass: 'generic-close-dialog',
      disableClose: true,
      width: '468px',
    });

    dialogRef.afterClosed().subscribe((confirmed) => {
      if (confirmed) {
        this.askMeService.deleteSession(this.sessionId).subscribe(() => {
          this.askMeService.getSession().subscribe((sessionData) => {
            this.sessionId = sessionData?.sessionId || '';
            this.suggestedQuestions = sessionData?.faqs;
            this.sessionStartedTime = new Date().toISOString();
            this.chatStarted = false;
            this.userQuestion = '';
            this.messages = [];
          });
        });
      }
    });
  };

  /**
   * Handles input change event for the user question input field.
   * Updates the userQuestion and toggles the showArrow flag.
   *
   * @param event The input event from the user.
   */
  onInputChange = (event: Event): void => {
    const target = event.target as HTMLInputElement;

    this.userQuestion = target.value;
    this.showArrow = this.userQuestion.trim().length > 0;
  };

  /**
   * Scrolls the chat history element to the bottom.
   */
  scrollToBottom = (): void => {
    try {
      const el = this.chatHistory?.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    } catch (err) {}
  };

  /**
   * Handles click on a suggested question.
   * Sets the userQuestion to the suggestion and focuses the input.
   *
   * @param suggestion The suggested question string.
   */
  onSuggestionClick = (suggestion: string): void => {
    this.userQuestion = suggestion;
    setTimeout(() => {
      const input = this.inputElement?.nativeElement;
      if (input) {
        input.focus();
        input.setSelectionRange(suggestion.length, suggestion.length);
      }
    });
  };

  /**
   * Handles click on the logo.
   * Closes the dialog and navigates to the home page.
   */
  handleLogoClick = (): void => {
    this.closeDialog();
    this.router.navigate(['']);
  };
}
